// Zone readings: the figures the live Real Time dashboards show for each zone.
//
// One place for the rules the single-area and project dashboards both apply:
// the night floor deduction (on the series, never on the raw figures beside
// it), the peak from each bucket's own max, the change against the sample ~1h
// before the latest, the traffic light, and how a Total group adds its zones.
// Pure: rows and floors in, readings out.

import type { RealtimeRow } from "../services/realtimeApi";
import type { RoamingZone } from "../components/realtime/RoamingPulse";

const HOUR_MS = 60 * 60 * 1000;

/** Deduct a zone's night floor from a count (never below zero). */
export function deductFloor(count: number, nightFloor: number): number {
  return Math.max(0, count - nightFloor);
}

export type Traffic = "up" | "steady" | "down" | "none";

/**
 * Traffic-light state for a zone on the map: rising, steady, falling, or no
 * data. "Steady" absorbs noise — a change within ±3% of the earlier value, or
 * within ±3 people for small counts — so the map doesn't flicker between red
 * and green on sampling jitter. Used by the present and international views
 * alike, so the colours mean the same thing whichever is showing.
 */
export function trafficOf(delta: number | null | undefined, base: number | null | undefined): Traffic {
  if (delta == null || base == null) return "none";
  const band = Math.max(Math.abs(base) * 0.03, 3);
  if (Math.abs(delta) <= band) return "steady";
  return delta > 0 ? "up" : "down";
}

export interface ZonePoint {
  x: number; // epoch ms
  y: number; // the count, floor deducted when the reading is adjusted
  max: number; // the bucket's own max (or the count), on the same scale as y
  yRaw: number;
  maxRaw: number;
}

export interface ZoneReading {
  /** The zone's rows, oldest → newest, as served. */
  rows: RealtimeRow[];
  /** The same rows with the floor deducted from count, min and max (= rows when not adjusted). */
  adjustedRows: RealtimeRow[];
  points: ZonePoint[];
  /** True when this zone's own night floor was deducted. */
  adjusted: boolean;
  now: number;
  nowRaw: number;
  latestAt: number;
  peak: number;
  peakRaw: number;
  /** The first row reaching the peak. */
  peakRow: RealtimeRow;
  /** Change since the sample ~1h before the latest. The floor cancels out of it. */
  delta: number;
  /** delta as a % of that earlier sample, on the adjusted scale. */
  pct: number;
  /** Judged against the raw earlier level. */
  traffic: Traffic;
  /** Raw samples behind the rows (bucketed: the sum of their sample counts). */
  sampleCount: number;
  /** Share of SUCCESS samples; null once bucketed (aggregated rows carry no result). */
  successPct: number | null;
}

const ts = (r: RealtimeRow) => +new Date(r.timestamp);

/**
 * The reading for one zone from its rows, or null when it has none yet.
 * `nightFloor` is deducted when given.
 */
export function readZone(
  rows: RealtimeRow[],
  opts: { nightFloor?: number | null; bucketed?: boolean } = {}
): ZoneReading | null {
  if (!rows.length) return null;
  const floor = opts.nightFloor ?? null;
  const sorted = [...rows].sort((a, b) => ts(a) - ts(b));
  const adjustedRows =
    floor == null
      ? sorted
      : sorted.map((r) => ({
          ...r,
          subscriber_count: deductFloor(r.subscriber_count, floor),
          ...(r.subscriber_count_min != null ? { subscriber_count_min: deductFloor(r.subscriber_count_min, floor) } : {}),
          ...(r.subscriber_count_max != null ? { subscriber_count_max: deductFloor(r.subscriber_count_max, floor) } : {}),
        }));
  const points: ZonePoint[] = sorted.map((r, i) => {
    const a = adjustedRows[i];
    return {
      x: ts(r),
      y: a.subscriber_count,
      max: a.subscriber_count_max ?? a.subscriber_count,
      yRaw: r.subscriber_count,
      maxRaw: r.subscriber_count_max ?? r.subscriber_count,
    };
  });

  const last = points[points.length - 1];
  const cut = last.x - HOUR_MS;
  let prior = points[0];
  for (const p of points) {
    if (p.x <= cut) prior = p;
    else break;
  }
  const delta = last.y - prior.y;

  let peakAt = 0;
  points.forEach((p, i) => {
    if (p.max > points[peakAt].max) peakAt = i;
  });

  const bucketed = !!opts.bucketed;
  return {
    rows: sorted,
    adjustedRows,
    points,
    adjusted: floor != null,
    now: last.y,
    nowRaw: last.yRaw,
    latestAt: last.x,
    peak: points[peakAt].max,
    peakRaw: Math.max(...points.map((p) => p.maxRaw)),
    peakRow: sorted[peakAt],
    delta,
    pct: prior.y ? (delta / prior.y) * 100 : 0,
    traffic: trafficOf(delta, last.yRaw - delta),
    sampleCount: bucketed ? sorted.reduce((n, r) => n + (r.sample_count ?? 0), 0) : sorted.length,
    successPct: bucketed ? null : Math.round((sorted.filter((r) => r.result === "SUCCESS").length / sorted.length) * 100),
  };
}

/**
 * Readings for a set of zones from one batch of rows. Every zone gets an
 * entry (null until it has data); `nightFloors` holds the floor to deduct per
 * zone, and a zone without one stays raw.
 */
export function readZones(
  rows: RealtimeRow[],
  zoneIds: string[],
  opts: { nightFloors?: ReadonlyMap<string, number>; bucketed?: boolean } = {}
): Map<string, ZoneReading | null> {
  const byZone = new Map<string, RealtimeRow[]>(zoneIds.map((id) => [id, []]));
  for (const r of rows) byZone.get(String(r.target_id))?.push(r);
  return new Map(
    zoneIds.map((id) => [
      id,
      readZone(byZone.get(id) ?? [], { nightFloor: opts.nightFloors?.get(id), bucketed: opts.bucketed }),
    ])
  );
}

/**
 * A Total group's figure: the sum over its zones that have a value, or null
 * when none has. Zones without a value are left out, not counted as 0.
 */
export function groupTotal(zoneIds: Iterable<string>, valueOf: (id: string) => number | null | undefined): number | null {
  let sum = 0;
  let any = false;
  for (const id of zoneIds) {
    const v = valueOf(id);
    if (v == null) continue;
    sum += v;
    any = true;
  }
  return any ? sum : null;
}

export interface LiveTotal {
  /** People present now, raw: the international figure beside it can't be floor-adjusted. */
  present: number | null;
  /** Only when there is roaming data at all. */
  intl?: number;
  intlDelta?: number;
}

/**
 * The live total for a Total group (`only`), or for every zone read (`only` null).
 */
export function liveTotal(
  readings: ReadonlyMap<string, ZoneReading | null>,
  roaming: RoamingZone[],
  only: ReadonlySet<string> | null
): LiveTotal {
  const ids = [...readings.keys()].filter((id) => !only || only.has(id));
  const present = groupTotal(ids, (id) => readings.get(id)?.nowRaw);
  if (!roaming.length) return { present };
  const zs = roaming.filter((z) => !only || only.has(z.id));
  return {
    present,
    intl: zs.reduce((s, z) => s + z.intlNow, 0),
    intlDelta: zs.reduce((s, z) => s + z.delta, 0),
  };
}
